import { useSyncExternalStore } from "react";

type AtomCallback<T> = (newValue: T) => void;
type AtomSetterPayload<T> = T | ((prev: T) => T);
type AtomSetter<T> = (p: AtomSetterPayload<T>) => void;

interface IAtom<T> {
	get: () => T;
	set: AtomSetter<T>;
	subscribe(cb: AtomCallback<T>): () => void;
}

export function atom<T>(initialValue: T): IAtom<T> {
	let value = initialValue;

	const subscribers = new Set<AtomCallback<T>>();

	const get = () => value;

	const set: IAtom<T>["set"] = (payload) => {
		value =
			typeof payload === "function"
				? (payload as (prev: T) => T)(value)
				: payload;
		subscribers.forEach((callback) => callback(value));
	};

	const subscribe = (callback: AtomCallback<T>) => {
		subscribers.add(callback);

		return () => {
			subscribers.delete(callback);
		};
	};

	return {
		get,
		set,
		subscribe,
	};
}

export function useAtom<T>(atom: IAtom<T>): [T, AtomSetter<T>] {
	return [useSyncExternalStore(atom.subscribe, atom.get), atom.set];
}
